import { MediaMatcher } from '@angular/cdk/layout';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { PerfectScrollbarConfigInterface } from 'ngx-perfect-scrollbar';
import { AuthService, BreadcrumbService, CompanyService, StateService, VersionCheckService } from 'src/app/services';
import { UserService } from 'src/app/services/user.service';
import { Role } from 'src/app/shared/models/role.model';
import { Company, ICompany } from 'identity-webapi-client';
import { FormControl } from '@angular/forms';
import { distinctUntilChanged, map, startWith, filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { SignalRService } from 'src/app/services/signal-r.service';

/** @title Responsive sidenav */
@Component({
  selector: 'app-full-layout',
  templateUrl: 'full.component.html',
  styleUrls: ['full.component.scss']
})
export class FullComponent implements OnDestroy, OnInit {
  mobileQuery: MediaQueryList;
  @ViewChild('companyInput') companyInput: ElementRef | undefined;

  dir = 'ltr';
  useDarkTheme = false;
  minisidebar = false;
  boxed = false;
  horizontal = false;

  green = false;
  blue = false;
  danger = false;
  showHide = false;
  url = '';
  sidebarOpened = false;
  status = false;
  isViewAsOpen = false;
  isAllowedToViewAs = false;
  isAdmin = false;
  isMultiCompany = false;

  loadingCompanies = false;
  companyAutocomplete = new FormControl();

  companies: ICompany[] = [];
  filteredCompanies: IDisplayCompany[] = [];
  selectedCompany: Company | undefined;
  companySearch = '';
  isMultiCompanyOpen = false;
  get isDefaultCompany() {
    return this.selectedCompany?.id && this.authService.getDefaultCompanyId() && this.selectedCompany?.id === this.authService.getDefaultCompanyId();
  }

  public showSearch = false;
  public config: PerfectScrollbarConfigInterface = {};
  // tslint:disable-next-line - Disables all
  private _mobileQueryListener: () => void;

  public get showLoadingBar() {
    return this.breadcrumbService.isLoading$;
  }

  private _subscriptions: Subscription[] = [];

  constructor(
    public router: Router,
    changeDetectorRef: ChangeDetectorRef,
    media: MediaMatcher,
    private stateService: StateService,
    private authService: AuthService,
    private userService: UserService,
    private breadcrumbService: BreadcrumbService,
    private companyService: CompanyService,
    private signalRService: SignalRService,
    private versionCheckService: VersionCheckService,
    private activatedRoute: ActivatedRoute
  ) {
    this.mobileQuery = media.matchMedia('(min-width: 1100px)');
    this._mobileQueryListener = () => changeDetectorRef.detectChanges();
    // tslint:disable-next-line: deprecation
    this.mobileQuery.addListener(this._mobileQueryListener);

    //this.versionCheckService.initVersionCheck();

    this._subscriptions.push(this.authService.userSubject.subscribe((user) => {
      this.isAdmin = this.authService.hasAnyRole([Role.SituSystemsDeveloper, Role.SituSystemsAdmin, Role.SituSystemsEmployee]);
      this.isAllowedToViewAs = this.isAdmin || !!this.authService.getOriginalUserId();
      this.isViewAsOpen = this.isAllowedToViewAs && (!!this.authService.getOriginalUserId() || stateService.state.viewAsOpen);

      this.loadCompanies();
      this.updateIsMultiCompany();

      if (this.isViewAsOpen) {
        this.showViewAs();
      }
      else {
        this.hideViewAs();
      }
    }));

    // Listen for route changes to auto-hide sidebar based on route data
    this._subscriptions.push(this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.checkRouteForSidebarConfig();
    }));

    this._subscriptions.push(this.stateService.state$.subscribe((state) => {
      this.selectedCompany = state.company as Company ?? undefined;
      this.useDarkTheme = state.useDarkTheme;
      this.companyAutocomplete.setValue(this.selectedCompany);

      this.updateIsMultiCompany();

      if (this.useDarkTheme) {
        this.addDarkMode();
      }
      else {
        this.removeDarkMode();
      }

      if (this.authService.isLoggedIn()) {
        var darkModeClaim: string = this.authService.getClaim("dark_mode");

        if (this.useDarkTheme != (darkModeClaim.toLowerCase() == 'true')) {
          this.userService.updateUserDarkMode(this.useDarkTheme);
        }
      }
    }));
  }

  ngOnInit(): void {
    this.companyAutocomplete.setValue(this.selectedCompany);
    this._subscriptions.push(this.companyAutocomplete.valueChanges.pipe(
      startWith(''),
      distinctUntilChanged(),
      map(value => {
        if (typeof value === 'string' || value instanceof String) {
          return value as string;
        }

        this.selectedCompany = value as Company ?? undefined;
      }),
      filter((value) => typeof value === 'string'),
      map(value => {
        this.companySearch = value?.toLowerCase() ?? '';
        this.loadCompanies();
      }))
      .subscribe());

      //this.signalRService.startConnection();
      
      // Check initial route for sidebar config
      this.checkRouteForSidebarConfig();
  }

  ngOnDestroy(): void {
    // tslint:disable-next-line: deprecation
    this.mobileQuery.removeListener(this._mobileQueryListener);
    if (this._subscriptions) {
      this._subscriptions.forEach(subscription => {
        if (subscription) {
          subscription.unsubscribe();
        }
      })
    }
  }

  getParentCompany(parentCompanyId: string): string {
    if (!parentCompanyId) return "";
    const parentCompany = this.companies.find(x => x.id === parentCompanyId);
    return parentCompany ? `< ${parentCompany.name}` : "";
  }

  showViewAs() {
    const body = document.getElementsByTagName('body')[0];
    body.classList.add('view-as');
    this.isViewAsOpen = true;
    this.stateService.state = {
      ...this.stateService.state,
      viewAsOpen: true
    };
  }

  hideViewAs() {
    const body = document.getElementsByTagName('body')[0];
    body.classList.remove('view-as');
    this.isViewAsOpen = false;
    this.stateService.state = {
      ...this.stateService.state,
      viewAsOpen: false
    };
  }

  displayCompany(company: ICompany) {
    return !!company ? `${company?.name}` : "";
  }

  updateIsMultiCompany() {
    this.isMultiCompany = (this.isAdmin && this.companies.length > 1) || this.authService.listCompanies().length > 1;
  }

  forceCompanySelection() {
    let value = this.companyAutocomplete.value;
    if (typeof value === 'string' || value instanceof String) {
      this.isMultiCompanyOpen = false;
    }
  }

  onCompanyChange(company: Company) {
    this.selectCompany(company);
    this.isMultiCompanyOpen = false;
  }

  onClickDefaultCompany() {
    this.selectCompany(this.authService.getDefaultCompany());
  }

  onClickChangeCompany() {
    if (!this.isMultiCompany) {
      return;
    }

    this.isMultiCompanyOpen = true;
    this.companyAutocomplete.setValue('');
    setTimeout(() => {
      this.companyInput?.nativeElement.focus();
    }, 1);
  }

  clickEvent(): void {
    this.status = !this.status;
  }

  darkClick() {
    this.useDarkTheme = !this.useDarkTheme;
    
    if (this.useDarkTheme) {
      this.addDarkMode();
    } else {
      this.removeDarkMode();
    }

    if (this.useDarkTheme != this.stateService.state.useDarkTheme) {
      this.stateService.state = {
        ...this.stateService.state,
        useDarkTheme: this.useDarkTheme
      };
    }
  }

  toggleMinisidebar() {
    this.minisidebar = !this.minisidebar;
  }

  private checkRouteForSidebarConfig() {
    // Get the activated route's data
    let route = this.activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }
    
    const routeData = route.snapshot.data;
    
    // Auto-hide sidebar if route specifies hideSidebar: true
    if (routeData && routeData.hideSidebar === true) {
      this.sidebarOpened = false;
    }
  }

  onClickShowViewAs() {
    if (this.isAllowedToViewAs) {
      this.showViewAs();
    }
  }

  onClickHideViewAs() {
    this.hideViewAs();
  }

  onClickSignOut() {
    this.authService.logout();
  }

  onClickSignIn() {
    this.authService.startAuthentication(this.router.url);
  }

  private toggleDarkMode() {
    const body = document.getElementsByTagName('body')[0];
    body.classList.toggle('dark');
  }

  private removeDarkMode() {
    const body = document.getElementsByTagName('body')[0];
    body.classList.remove('dark');
  }

  private addDarkMode() {
    const body = document.getElementsByTagName('body')[0];
    body.classList.add('dark');
  }

  private loadCompanies() {
    this.companyService.companies$.subscribe((companies) => {
      this.updateCompanies(companies);
    });
  }

  private updateCompanies(companies: ICompany[]) {
    this.companies = companies ?? [];
    this.updateIsMultiCompany();
    if (this.companySearch) {
      this.filteredCompanies = this.orderCompanies(this._filterCompanies(companies)) ?? [];
    } else {
      this.filteredCompanies = this.orderCompanies(companies);
    }
  }

  private orderCompanies(filteredCompanies: ICompany[]): IDisplayCompany[] {
    const map: { [key: string]: IDisplayCompany[] } = {};

    const orderedCompanies: IDisplayCompany[] = filteredCompanies.map((company) => ({
        ...company,
        displayName: company.name,
    }));

    // Check for parent existence in filtered companies
    const parentExists = (companyId: string) => orderedCompanies.some(company => company.id === companyId);

    // Associate each company with its parent, if the parent exists in the filtered list
    orderedCompanies.forEach((company) => {
        if (!company.parentCompanyId || !parentExists(company.parentCompanyId)) {
            return;
        }

        // Initialize the key if it does not exist in the map
        if (!map[company.parentCompanyId]) {
            map[company.parentCompanyId] = [];
        }

        // Add the company to its parent's list in the map
        map[company.parentCompanyId].push(company);
    });

    const updateDisplayName = (company: IDisplayCompany) => {
        company.displayName = company?.parentCompanyId ? `${company.name} ${this.getParentCompany(company.parentCompanyId)}` : company.name;

        if (map[company.id!]) {
            map[company.id!].forEach((subCompany) => {
                updateDisplayName(subCompany);
            });
        }
    };

    // Start the process with the companies that do not have a parent, or whose parent is not in the list
    orderedCompanies.forEach((company) => {
        if (!company.parentCompanyId || !parentExists(company.parentCompanyId)) {
            updateDisplayName(company);
        }
    });

    // Sort companies by displayName
    orderedCompanies.sort((a, b) => {
        if (a.displayName! < b.displayName!) {
            return -1;
        } else if (a.displayName! > b.displayName!) {
            return 1;
        } else {
            return 0;
        }
    });

    return orderedCompanies;
  }

  private selectCompany(company: Company | null) {
    if (this.selectedCompany != company) {
      this.selectedCompany = company as Company ?? undefined;
    }

    if (this.stateService.state.company != this.selectedCompany) {
      this.stateService.state = {
        ...this.stateService.state,
        company
      };
    }
  }

  private _filterCompanies(companies: ICompany[]): ICompany[] {
    return companies.filter((c) => c.name?.toLowerCase().includes(this.companySearch.toLowerCase()));
  }
}

export interface IDisplayCompany extends ICompany {
  displayName?: string;
}
